import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Phone } from "lucide-react";
import {
  whosCallingApi,
  type WhosCallingBuyerDto,
  type WhosCallingListingDto,
  type WhosCallingParams,
} from "@/api/whosCalling";
import type { ListingAgentBriefDto } from "@/api/buyers";
import {
  Button,
  buttonVariants,
  EmptyState,
  FormField,
  Input,
  Skeleton,
  StatusBadge,
} from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { formatCurrency } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import { whosCallingKeys } from "@/hooks/buyers/whosCallingKeys";
import { useDebounced } from "@/hooks/buyers/useSmartImport";
import { useOptionalAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { LogCommunicationModal } from "@/pages/communications/components/LogCommunicationModal";
import { buyerLabel } from "@/pages/buyers/buyerFilters";
import { submissionStatusTone } from "@/pages/buyers/buyerListBits";
import { listingAgentLine } from "@/pages/buyers/listingAgentColumn";
import { QueryErrorState } from "@/pages/buyers/queryStates";

/** The server's shortest searching query (WhosCallingService.MinQueryLength). */
export const WHOS_CALLING_MIN_QUERY = 2;

/** Both reads the panel joins: the endpoint is Listings.Read with an in-body
 *  Submissions.Read (ListingsController.WhosCalling). Optional auth, like
 *  every gated affordance: with no provider the doors simply do not render. */
export function useCanUseWhosCalling(): boolean {
  const auth = useOptionalAuth();
  return !!auth?.hasPermission("Listings.Read") && !!auth?.hasPermission("Submissions.Read");
}

/**
 * Addendum B (B2, B3) — "Who's calling?". A broker calls back or replies:
 * from any fragment of them (part of a phone number in any format, an email,
 * a name, a firm, a listing's title, city or reference), their listings and
 * every buyer presented on each, with the last message sent to the broker.
 *
 * <p>With <code>agentId</code> (the Agents directory's VIEW DEALS) the panel
 * opens on that agent's deals and has no search box.</p>
 *
 * <p>It writes nothing. LOG CALL opens the existing log form, prefilled with
 * the submission, channel Call and counterparty Listing agent; the specialist
 * still saves it.</p>
 */
export function WhosCallingPanel({
  open,
  onClose,
  agent,
}: {
  open: boolean;
  onClose: () => void;
  /** VIEW DEALS: one agent's listings and buyers. */
  agent?: { id: string; label: string };
}) {
  const [search, setSearch] = useState("");
  const [logFor, setLogFor] = useState<{ buyer: WhosCallingBuyerDto; listingName: string } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const settled = useDebounced(search, 250).trim();

  const params: WhosCallingParams = agent ? { agentId: agent.id } : { q: settled };
  const searching = agent !== undefined || settled.length >= WHOS_CALLING_MIN_QUERY;
  const result = useQuery({
    queryKey: whosCallingKeys.search(params),
    queryFn: () => whosCallingApi.search(params),
    enabled: open && searching,
  });

  const close = () => {
    setSearch("");
    onClose();
  };

  const data = result.data;
  const nothing = data !== undefined && data.agents.length === 0 && data.listingsWithNoAgent.length === 0;
  const more = data !== undefined && (data.moreAgents || data.moreListings);
  const rowProps = { onOpen: close, onLogCall: (buyer: WhosCallingBuyerDto, listingName: string) => setLogFor({ buyer, listingName }) };

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={agent ? `Listings and buyers — ${agent.label}` : "Who's calling?"}
        description={
          agent
            ? "Every listing this agent represents, and every buyer presented on each."
            : "Find a broker from anything they give you, and every buyer presented on their listings."
        }
        size="lg"
        initialFocusRef={agent ? undefined : searchRef}
        footer={
          <Button variant="text" onClick={close}>
            Done
          </Button>
        }
      >
        <div className="grid gap-space-4" data-testid="whos-calling-panel">
          {!agent && (
            <FormField label="Search">
              <Input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Phone, email, name, firm, or listing title, city or reference"
                aria-label="Search brokers and listings"
              />
            </FormField>
          )}

          {!searching ? (
            <p className="text-small text-gray-mid m-0" data-testid="whos-calling-hint">
              Type part of a phone number (any format), an email, a name, a firm, or a listing&rsquo;s
              title, city or reference.
            </p>
          ) : result.isError ? (
            <QueryErrorState what="the matches" onRetry={() => void result.refetch()} retrying={result.isFetching} />
          ) : data === undefined ? (
            <Skeleton className="h-40 w-full" />
          ) : nothing ? (
            <EmptyState
              title="No matches"
              description={agent ? "This agent has no listings on file." : `Nothing matches “${settled}”.`}
            />
          ) : (
            <div className="grid gap-space-4" data-testid="whos-calling-results">
              {more && (
                <p className="text-caption text-gray-mid m-0" data-testid="whos-calling-more">
                  {agent
                    ? "Showing this agent's 50 newest listings."
                    : "Keep typing to narrow — showing the first 20 agents and 50 listings."}
                </p>
              )}
              {data.agents.map((group) => (
                <section
                  key={group.agent.id}
                  className="rounded-md border border-border-light p-space-3 grid gap-space-3"
                  data-testid={`whos-calling-agent-${group.agent.id}`}
                >
                  <AgentHeader agent={group.agent} />
                  {group.listings.length === 0 ? (
                    // With listings cut at the cap, an agent who matched by name
                    // may have listings past it — "none on file" would be untrue.
                    <p className="text-small text-gray-mid m-0" data-testid="whos-calling-agent-no-listings">
                      {data.moreListings
                        ? "Listings not shown — keep typing to narrow, or use VIEW DEALS in the Agents directory."
                        : "No listings on file for this agent."}
                    </p>
                  ) : (
                    group.listings.map((listing) => (
                      <ListingBlock key={listing.listingId} listing={listing} {...rowProps} />
                    ))
                  )}
                </section>
              ))}
              {data.listingsWithNoAgent.length > 0 && (
                <section
                  className="rounded-md border border-border-light p-space-3 grid gap-space-3"
                  data-testid="whos-calling-no-agent"
                >
                  <h3 className="text-small font-semibold text-onyx m-0">Listings with no agent on file</h3>
                  {data.listingsWithNoAgent.map((listing) => (
                    <ListingBlock key={listing.listingId} listing={listing} {...rowProps} />
                  ))}
                </section>
              )}
            </div>
          )}
        </div>
      </Modal>

      {logFor && (
        <LogCommunicationModal
          open
          onClose={() => setLogFor(null)}
          preFilledOwner={{
            type: "buyer_submission",
            id: logFor.buyer.submissionId,
            label: `${logFor.buyer.buyerName} — ${logFor.listingName}`,
          }}
          preFilledChannel="Call"
          preFilledCounterparty="ListingAgent"
        />
      )}
    </>
  );
}

function AgentHeader({ agent }: { agent: ListingAgentBriefDto }) {
  return (
    <div className="grid gap-space-1">
      <h3 className="text-small font-semibold text-onyx m-0">{listingAgentLine(agent)}</h3>
      <div className="flex flex-wrap gap-x-space-4 gap-y-space-1 text-small">
        <CopyValue label="phone" value={agent.phone} />
        <CopyValue label="email" value={agent.email} />
      </div>
    </div>
  );
}

/**
 * A contact value with a copy button that tells the truth: a missing Clipboard
 * API or a refused write says so rather than claiming "Copied" (the
 * PrequalSiteInvitesSection precedent).
 */
function CopyValue({ label, value }: { label: string; value?: string | null }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const text = value?.trim();
  if (!text) return <span className="text-gray-mid">No {label} on file</span>;

  const copy = async () => {
    if (!navigator.clipboard?.writeText) {
      setState("failed");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch {
      setState("failed");
    }
  };

  return (
    <span className="inline-flex items-center gap-space-1 min-w-0">
      <span className="break-all text-onyx">{text}</span>
      <Button variant="text" size="sm" onClick={() => void copy()} aria-label={`Copy ${label} ${text}`}>
        {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy"}
      </Button>
    </span>
  );
}

function ListingBlock({
  listing,
  onOpen,
  onLogCall,
}: {
  listing: WhosCallingListingDto;
  onOpen: () => void;
  onLogCall: (buyer: WhosCallingBuyerDto, listingName: string) => void;
}) {
  const place = [listing.city, listing.state].filter((p) => p?.trim()).join(", ");
  return (
    <div className="grid gap-space-2" data-testid={`whos-calling-listing-${listing.listingId}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-space-2">
        <div className="min-w-0">
          <div className="text-onyx font-semibold">{listing.displayName}</div>
          <div className="text-caption text-gray-mid">
            {[place || null, listing.askingPrice != null ? `Asking ${formatCurrency(listing.askingPrice)}` : null]
              .filter(Boolean)
              .join(" · ") || "No location or asking price on file"}
          </div>
        </div>
        <Link
          to={`/listings/${listing.listingId}`}
          onClick={onOpen}
          className={buttonVariants({ variant: "text", size: "sm" })}
        >
          Open listing
        </Link>
      </div>
      {listing.buyers.length === 0 ? (
        <p className="text-caption text-gray-mid m-0">No buyers presented on this listing.</p>
      ) : (
        <ul className="grid gap-space-2 m-0 p-0 list-none">
          {listing.buyers.map((buyer) => (
            <BuyerRow
              key={buyer.submissionId}
              buyer={buyer}
              onOpen={onOpen}
              onLogCall={() => onLogCall(buyer, listing.displayName)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function BuyerRow({
  buyer,
  onOpen,
  onLogCall,
}: {
  buyer: WhosCallingBuyerDto;
  onOpen: () => void;
  onLogCall: () => void;
}) {
  const fmt = useDateFormatter();
  const last = buyer.lastMessageToBroker;
  return (
    <li
      className="flex flex-wrap items-center justify-between gap-space-2 rounded-sm bg-white-warm px-space-3 py-space-2"
      data-testid={`whos-calling-buyer-${buyer.submissionId}`}
    >
      <div className="min-w-0 grid gap-space-1">
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="text-onyx">{buyer.buyerName}</span>
          <StatusBadge tone={submissionStatusTone(buyer.status)}>{buyerLabel(buyer.status)}</StatusBadge>
        </div>
        <div className="text-caption text-gray-mid">
          {buyer.ownerName ? `Owner: ${buyer.ownerName}` : "No owner assigned"}
        </div>
        <div className="text-caption text-gray-mid" data-testid="whos-calling-last-message">
          {last
            ? `Last to the broker: ${last.channel}, ${fmt.date(last.sentAtUtc)}${last.sentByName ? `, by ${last.sentByName}` : ""}`
            : "Nothing sent to the broker yet"}
        </div>
      </div>
      <div className="flex items-center gap-space-1">
        <Link
          to={`/buyer-submissions/${buyer.submissionId}`}
          onClick={onOpen}
          className={buttonVariants({ variant: "text", size: "sm" })}
          aria-label={`Open ${buyer.buyerName}'s submission`}
        >
          Open
        </Link>
        <Button variant="secondary" size="sm" onClick={onLogCall} aria-label={`Log a call about ${buyer.buyerName}`}>
          Log call
        </Button>
      </div>
    </li>
  );
}

/** The top bar's WHO'S CALLING? icon (every staff page), beside the
 *  Communications log icon and styled like it. */
export function WhosCallingNavButton() {
  const [open, setOpen] = useState(false);
  const allowed = useCanUseWhosCalling();
  if (!allowed) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Who's calling?"
        title="Who's calling?"
        data-testid="whos-calling-nav"
        className={cn(
          "relative inline-flex items-center justify-center h-10 w-10 rounded-md",
          "text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5",
          "focus-visible:outline-none focus-visible:shadow-goldRing",
        )}
      >
        <Phone size={18} strokeWidth={1.5} aria-hidden />
      </button>
      <WhosCallingPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** The Submissions page's WHO'S CALLING? button, beside PRESENT A BUYER. */
export function WhosCallingButton() {
  const [open, setOpen] = useState(false);
  const allowed = useCanUseWhosCalling();
  if (!allowed) return null;
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)} data-testid="whos-calling-open">
        <Phone size={14} strokeWidth={1.5} aria-hidden /> Who&rsquo;s calling?
      </Button>
      <WhosCallingPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}
